import { createContext, FunctionComponent, useContext, useEffect, useState } from 'react';

// Types
import { Doctor } from '../[id]/DoctorPage';

// Contexts
import { useAlertContext } from '@Components/Alert/Context';
import { getHttpErrorMessage, useHttpContext } from '@Libs/http';

// Utilities
import doctorApi from '@Libs/api/user/doctor';

interface DoctorQueries {
    keyword: string;
    filterSpecialty: string;
}

interface DoctorsContextType {
    hasMore: boolean;
    doctors: Doctor[];
    loadMore(queries: DoctorQueries, offset?: number): void;
}

const initState: DoctorsContextType = {
    doctors: [],
    hasMore: false,
    loadMore: () => null,
};

const DoctorsContext = createContext<DoctorsContextType>(initState);

export const useDoctorsManager = (): DoctorsContextType => useContext(DoctorsContext);

interface ProviderProps {
    limit: number;
    keyword?: string;
    doctors: Doctor[];
    filterSpecialty: string;
}

export const Provider: FunctionComponent<ProviderProps> = (props) => {
    const { alert } = useAlertContext();
    const { userHttp } = useHttpContext();

    const [doctors, setDoctors] = useState<Doctor[]>(props.doctors || []);
    const [hasMore, setHasMore] = useState<boolean>(props.doctors.length >= props.limit);

    useEffect(() => {
        setDoctors(props.doctors);
        setHasMore(props.doctors.length >= props.limit);
    }, [props.keyword, props.filterSpecialty]);

    const loadMore = async (queries: DoctorQueries, offset = 0) => {
        if (hasMore) {
            try {
                const { doctors } = await doctorApi(userHttp).search(
                    {
                        ...(queries.keyword && { q: queries.keyword }),
                        ...(queries.filterSpecialty && { filterSpecialties: [queries.filterSpecialty] }),
                    },
                    offset,
                    props.limit,
                );

                setHasMore(doctors.length >= props.limit);
                setDoctors((currentDoctors) => [...currentDoctors, ...doctors]);
            } catch (error) {
                alert({
                    type: 'error',
                    title: 'Đã xảy ra lỗi',
                    text: getHttpErrorMessage(error),
                });
            }
        }
    };

    return <DoctorsContext.Provider value={{ doctors, hasMore, loadMore }}>{props.children}</DoctorsContext.Provider>;
};

Provider.defaultProps = {
    doctors: [],
};

type WithDoctorsManager<T> = T & ProviderProps;

export function withDoctorsManager<P>(WrappedComponent: FunctionComponent) {
    return ({ limit = 10 }): FunctionComponent<WithDoctorsManager<P>> => {
        return ({ doctors, keyword, filterSpecialty, ...props }) => {
            return (
                <Provider doctors={doctors} keyword={keyword} filterSpecialty={filterSpecialty} limit={limit}>
                    <WrappedComponent doctors={doctors} filterSpecialty={filterSpecialty} keyword={keyword} {...props} />
                </Provider>
            );
        };
    };
}
